import * as R from "ramda";
import { getTime, format } from "date-fns";
import {
  CurvesTypes,
  getCurveName,
} from "../redux/modules/productionData/production";

const anyFalse = (rows: any[]): boolean =>
  Array.isArray(rows) && rows.some((r) => r.certain === false);

const allNil = (rows: any[]): boolean =>
  Array.isArray(rows) && rows.every((r) => r == null || r.value == null);

const getDailyValue = (rows: any[], certain: boolean): number | null => {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const normalized = anyFalse(rows)
    ? rows.map((r) => ({ ...r, certain: false }))
    : rows;
  const filtered = normalized.filter((r) => r.certain === certain);
  if (filtered.length === 0 || allNil(filtered)) return null;
  return filtered.reduce((sum, r) => sum + (r.value || 0), 0);
};

const getDailyInfo = (data: any[], certain: boolean) => {
  if (!Array.isArray(data) || data.length === 0) return [];
  const groups: Record<string, any[]> = {};
  for (const row of data) {
    const key = format(new Date(row.dateTimeOffset), "yyyy-MM-dd");
    (groups[key] = groups[key] || []).push(row);
  }
  const result = Object.entries(groups).map(([day, rows]) => ({
    x: getTime(new Date(day)),
    y: getDailyValue(rows, certain),
    borderColor: "Yellow",
  }));
  result.sort((a, b) => a.x - b.x);
  return result;
};

const getHourlyInfo = (data: any[], certain: boolean) => {
  if (!Array.isArray(data) || data.length === 0) return [];

  // Se esiste almeno un certo = false, forziamo tutti a false
  const hasAnyFalse = data.some((r) => r && r.certain === false);
  const normalized = hasAnyFalse
    ? data.map((r) => ({ ...r, certain: false }))
    : data.slice();

  // Aggiunge _hourTs e prepara gruppi
  const groups: Record<number, any[]> = {};
  for (const row of normalized) {
    const d = new Date(row.dateTimeOffset);
    d.setMinutes(0, 0, 0);
    const hourTs = d.getTime();
    (groups[hourTs] = groups[hourTs] || []).push(row);
  }

  const result = Object.entries(groups).map(([hourTsStr, rows]) => {
    const hourTs = Number(hourTsStr);
    const filtered = rows.filter(
      (r) => r.certain === certain && r.value !== null && r.value !== undefined
    );

    if (filtered.length === 0) return { x: hourTs, y: null };

    // Quarterly se almeno un elemento ha minuti diversi da 0
    const isQuarterlyGroup = rows.some(
      (r) => new Date(r.dateTimeOffset).getMinutes() !== 0
    );

    const y = isQuarterlyGroup
      ? filtered.reduce((s, r) => s + (r.value || 0), 0)
      : filtered[0].value !== undefined
      ? filtered[0].value
      : null;

    return { x: hourTs, y };
  });

  result.sort((a, b) => a.x - b.x);
  return result;
};

const expandHourlyToQuarterly = (rows: any[], tideTs: number) =>
  R.pipe<any[], any[], any[]>(
    R.chain((row: any) => {
      const dt = new Date(row.dateTimeOffset);
      const ts = dt.getTime();
      // Se è dopo (o uguale) al tide o già non è un'ora esatta, non espandere
      if (ts >= tideTs || dt.getMinutes() !== 0) {
        return [row];
      }
      // Crea i 4 quarti duplicando proprietà e dividendo il valore per 4
      return [0, 15, 30, 45].map((m) => {
        const q = new Date(ts);
        q.setMinutes(m, 0, 0);
        return {
          ...row,
          dateTimeOffset: q.toISOString(),
          value: row.value ? row.value / 4 : null,
        };
      });
    }),
    R.sortBy((r: any) => new Date(r.dateTimeOffset).getTime())
  )(rows);
const getQuarterlyInfo = (
  data: any,
  certain: boolean,
  tideDate?: Date | string | number
) => {
  const tideTs =
    tideDate != null ? new Date(tideDate as any).getTime() : undefined;

  if (!data || R.isEmpty(data)) {
    return [];
  }

  let normalized = data;
  if (tideTs != null) {
    const timeStamps = R.map(
      (r: any) => new Date(r.dateTimeOffset).getTime(),
      data
    ) as number[];
    const minTs = Math.min(...timeStamps);
    const maxTs = Math.max(...timeStamps);
    const shouldExpand = tideTs > minTs && tideTs < maxTs;
    if (shouldExpand) {
      normalized = expandHourlyToQuarterly(data, tideTs);
    }
  }

  return R.map((x: any) => {
    const cert = R.pathOr(false, ["certain"], x);
    return {
      x: getTime(new Date(x.dateTimeOffset)),
      y: R.equals(cert, certain) ? R.pathOr(null, ["value"], x) : null,
    };
  }, normalized);
};
export const getCurveColumnColor = (name: CurvesTypes) => {
  switch (name) {
    case "settlement":
      return "#ffbfbf";
    case "unbalance":
    default:
      return "#e6e6e6";
  }
};
export const getCurveColor = (name: CurvesTypes) => {
  switch (name) {
    case "settlement":
      return "#e10019";
    case "spread":
      return "#761613";
    case "net":
      return "#cfa2a7";
    case "production":
      return "#ed6675";
    case "forecast":
      return "#9e9294";
    case "unbalance":
    default:
      return "#000000";
  }
};

export const getProduzioneSeries = ({
  data,
  curves,
  granularity,
  tideDate,
}: {
  data: any;
  curves: any;
  granularity: any;
  tideDate?: Date | string | number;
}) => {
  return R.ifElse(
    (x) => R.isEmpty(x) || R.isNil(x),
    () => [],
    R.pipe<any, any, any, any, any>(
      R.toPairs,
      R.map(([curve, val]) => {
        const colObj = {
          type: "column",
          marker: {
            enabled: false,
            symbol: "circle",
          },
          name: getCurveName(curve),
          color: getCurveColor(curve),
          turboThreshold: 1000000,
          curveName: curve,
        };
        const lineObj = {
          type: "line",
          marker: {
            enabled: false,
            symbol: "circle",
          },
          name: getCurveName(curve),
          color: getCurveColor(curve),
          turboThreshold: 1000000,
          curveName: curve,
        };
        const certainTrueData = R.equals(granularity, "daily")
          ? getDailyInfo(val.timeseries, true)
          : R.equals(granularity, "quarterly")
          ? getQuarterlyInfo(val.timeseries, true, tideDate)
          : getHourlyInfo(val.timeseries, true);

        const certainFalseData = R.equals(granularity, "daily")
          ? getDailyInfo(val.timeseries, false)
          : R.equals(granularity, "quarterly")
          ? getQuarterlyInfo(val.timeseries, false, tideDate)
          : getHourlyInfo(val.timeseries, false);

        return R.equals(curve, "unbalance")
          ? [
              {
                ...colObj,
                data: certainTrueData,
              },
              {
                ...colObj,
                opacity: 0.5,
                name: getCurveName(curve) + " (provvisoria)",
                data: certainFalseData,
              },
            ]
          : [
              {
                ...lineObj,
                dashStyle: "Solid",
                data: certainTrueData,
              },
              {
                ...lineObj,
                dashStyle: "Dash",
                name: getCurveName(curve) + " (provvisoria)",
                data: certainFalseData,
              },
            ];
      }),
      R.flatten,
      R.reject((row: any) => !R.contains(row.curveName, curves || []))
    )
  )(data);
};

export const getSettlementSeries = ({
  data,
  curves,
  granularity,
  tideDate,
}: {
  data: any;
  curves: any;
  granularity: any;
  tideDate?: Date | string | number;
}) => {
  return R.ifElse(
    (x) => R.isEmpty(x) || R.isNil(x),
    () => [],
    R.pipe<any, any, any, any, any>(
      R.toPairs,
      R.map(([curve, val]) => {
        const colObj = {
          type: "column",
          marker: {
            enabled: false,
            symbol: "circle",
          },
          color: getCurveColor(curve),
          turboThreshold: 1000000,
          curveName: curve,
        };

        const certainTrueData = R.equals(granularity, "daily")
          ? getDailyInfo(val.timeseries, true)
          : R.equals(granularity, "quarterly")
          ? getQuarterlyInfo(val.timeseries, true, tideDate)
          : getHourlyInfo(val.timeseries, true);

        const certainFalseData = R.equals(granularity, "daily")
          ? getDailyInfo(val.timeseries, false)
          : R.equals(granularity, "quarterly")
          ? getQuarterlyInfo(val.timeseries, false, tideDate)
          : getHourlyInfo(val.timeseries, false);

        return [
          {
            ...colObj,
            name: getCurveName(curve),
            data: certainTrueData,
          },
          {
            ...colObj,
            opacity: 0.5,
            name: getCurveName(curve) + " (provvisoria)",
            data: certainFalseData,
          },
        ];
      }),
      R.flatten,
      R.reject((row: any) => !R.contains(row.curveName, curves || []))
    )
  )(data);
};

export const getAllPlants = R.ifElse(
  (x) => R.isEmpty(x) || R.isNil(x),
  () => [],
  R.pipe(
    R.toPairs,
    R.map(([curve, val]) => {
      return R.isNil(curve) || R.isEmpty(val)
        ? []
        : R.pipe(
            R.pathOr([], ["statistics"]),
            R.map(R.pathOr(null, ["pvi"]))
          )(val);
    }),
    R.flatten,
    R.uniq
  )
);

export const getAllPlantsNew = (x: any) => {
  const val = R.ifElse(
    (x) => R.isEmpty(x) || R.isNil(x),
    () => [],
    R.pipe(
      R.toPairs,
      R.map(([curve, val]) => {
        return R.isNil(curve) || R.isEmpty(val)
          ? []
          : R.pipe(
              R.pathOr([], ["statistics"]),
              R.map((x) => {
                return {
                  pvi: R.pathOr(null, ["pvi"])(x),
                  partitaIva: R.pathOr(null, ["partitaIva"])(x),
                };
              })
            )(val);
      }),
      R.flatten,
      R.uniq
    )
  )(x);
  return val;
};
type GetCurveDataType = { data: any; plant: any; curve: CurvesTypes };
export const getCurveData = ({ data, plant, curve }: GetCurveDataType) =>
  R.pipe(
    R.pathOr(null, [curve]),
    R.pathOr([], ["statistics"]),
    R.filter(R.propEq("pvi", plant)),
    R.map(R.pathOr(null, ["sum"])),
    (x) => {
      return R.isEmpty(x) || R.isNil(x) || R.all(R.isNil)(x)
        ? null
        : x.reduce((a: any, b: any) => a + b || null, 0);
    }
  )(data);

type GetAllDataSum = { data: any; curve: CurvesTypes; prop: "sum" | "absSum" };
export const getAllDataSum = ({ data, curve, prop }: GetAllDataSum) =>
  R.pipe(
    R.pathOr(null, [curve]),
    R.pathOr([], ["statistics"]),
    R.map(R.pathOr(null, [prop])),
    (x) =>
      R.isEmpty(x) || R.isNil(x) || R.all(R.isNil)(x)
        ? null
        : x.reduce((a: any, b: any) => a + (b || 0), 0)
  )(data);

export const allPvis = R.pipe<any, any, any>(
  R.map(R.path(["pvi", "pvi"])),
  R.values
);

export const ragioneSocialeVal = ({
  filters,
  data,
}: {
  filters: any;
  data: any;
}) =>
  R.pipe<any, any, any>(
    R.filter(R.propEq("partitaIva", filters)),
    (x) => {
      return R.isEmpty(x)
        ? []
        : [{ value: x[0].partitaIva, title: x[0].ragioneSociale }];
    }
  )(data);
